import { type ReactNode, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Box } from '@repo/ui/Box';
import { Button } from '@repo/ui/Button';
import { Dialog } from '@repo/ui/Dialog';
import { DialogClose } from '@repo/ui/Dialog/DialogClose';
import { DialogContent } from '@repo/ui/Dialog/DialogContent';
import { DialogHeader } from '@repo/ui/Dialog/DialogHeader';
import { DialogTrigger } from '@repo/ui/Dialog/DialogTrigger';
import { useDialog } from '@repo/ui/Dialog/DialogRoot';
import { DialogTitle } from '@repo/ui/Dialog/DialogTitle';
import { Flex } from '@repo/ui/Flex';
import { Icon } from '@repo/ui/Icon';
import { IconButton } from '@repo/ui/IconButton';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { LocationPreviewCard } from './LocationPreviewCard';
import type { Location } from '../../graphql/query/getDelivery';
import { useDeliveryOptions } from '../../hooks/useDeliveryOptions';
import { Text } from '../Language/Language';
import { SearchBox } from './SearchBox';

import s from './LocationPicker.module.css';
import { getDeliveryType } from '../tasks/MethodTask/helpers';
import { hoursToString } from './openingHours';
import { formatLocationAddressMulti } from '../../utils/address';

type LocationButton = Location & { onSelect: (props: LocationButton) => void };

const LocationButton = (props: LocationButton) => {
  const {
    id,
    storeName,
    address,
    logoUrl,
    distance,
    onSelect,
    openingHours: locationHours,
  } = props;
  const addressSections = formatLocationAddressMulti(address);
  const { close } = useDialog();

  const handleSelect = () => {
    onSelect(props);
    close();
  };

  return (
    <Flex
      className={s.locationWrapper}
      alignItems="center"
      gap="g-8"
      padding="py-24"
      key={id}
      onClick={handleSelect}
    >
      <LocationPreviewCard
        title={storeName}
        description={
          <Box>
            {addressSections.map((addressLine) => (
              <Box key={addressLine}>{addressLine}</Box>
            ))}
          </Box>
        }
        icon={logoUrl}
        attribute={`${distance ?? ''}`}
        hours={hoursToString({ locationHours })}
      />
    </Flex>
  );
};

const MessageWrapper = ({ children }: { children: ReactNode }) => (
  <Flex
    padding="p-48"
    direction="column"
    alignItems="center"
    className={s.messageWrapper}
    gap="g-12"
  >
    {children}
  </Flex>
);

const NoLocations = () => {
  return (
    <MessageWrapper>
      <Icon name="map" size="lg" />
      <div className={s.noLocationTitle}>
        <Text langKey="pdx.delivery.location_picker.no_location.title" />
      </div>
      <div>
        <Text langKey="pdx.delivery.location_picker.no_location.body" />
      </div>
    </MessageWrapper>
  );
};

const Loader = ({ repeat = 5 }: { repeat?: number }) => (
  <SkeletonLoader
    padding="py-24"
    gap={['g-48']}
    itemHeight={30}
    layout={{
      repeat,
      items: {
        default: [
          {
            columns: 1,
            rows: 1,
          },
          {
            columns: 7,
            rows: 1,
            startColumn: 2,
            startRow: 1,
          },
          {
            columns: 5,
            rows: 1,
            startColumn: 2,
            startRow: 2,
          },
          {
            columns: 2,
            rows: 1,
            startColumn: 11,
            startRow: 1,
          },
        ],
        mobile: [
          {
            columns: 2,
            rows: 1,
          },
          {
            columns: 6,
            rows: 1,
            startColumn: 3,
            startRow: 1,
          },
          {
            columns: 4,
            rows: 1,
            startColumn: 3,
            startRow: 2,
          },
          {
            columns: 3,
            rows: 1,
            startColumn: 10,
            startRow: 1,
          },
        ],
      },
    }}
  />
);

export const LocationPickerTrigger = () => (
  <DialogTrigger asChild={true}>
    <Button variant="secondary">
      <Icon name="location" />
      <Text langKey="pdx.delivery.location_preview.see_more" />
    </Button>
  </DialogTrigger>
);

export const LocationPicker = () => {
  const { setValue, watch } = useFormContext();
  const { isLoading, deliveryOptions, methodDropped, orderPostalCode } =
    useDeliveryOptions();

  const [postalCodeHistory, setPostalCodeHistory] = useState<string[]>(
    orderPostalCode ? [orderPostalCode] : [],
  );
  const [missingMethodError, setMissingMethodError] = useState(false);

  const selectedMethod = watch('returnMethod.type');

  const method = deliveryOptions?.find((e) => e.id === selectedMethod);
  const locations = method?.locations;

  const onSelect = ({ id }: LocationButton) => {
    setValue('returnMethod.location', id);
  };

  const onUpdatePostalCode = (postalCode: string) => {
    setMissingMethodError(false);
    setValue('locations.postalCode', postalCode);
    setPostalCodeHistory([...postalCodeHistory, postalCode]);
  };

  useEffect(() => {
    if (methodDropped) {
      const previousPostalCode =
        postalCodeHistory[postalCodeHistory.length - 2];
      if (previousPostalCode) {
        setMissingMethodError(true);
        setValue('locations.postalCode', previousPostalCode);
        setPostalCodeHistory([...postalCodeHistory, previousPostalCode]);
      }
    }
  }, [
    methodDropped,
    postalCodeHistory,
    setMissingMethodError,
    setValue,
    setPostalCodeHistory,
  ]);

  let deliveryType;

  if (method) {
    const {
      id,
      packagingOption,
      serviceProvider: { name },
    } = method;

    deliveryType = getDeliveryType({ packagingOption, name, id });
  }

  return (
    <Dialog>
      <DialogHeader>
        <DialogTitle position="center">
          <Text
            langKey={[
              `pdx.delivery.location_picker.${deliveryType?.toLowerCase()}.title`,
              'pdx.delivery.location_picker.title',
            ]}
          />
        </DialogTitle>
        <DialogClose asChild={true}>
          <IconButton
            icon="close"
            label={<Text langKey="pdx.global.close" />}
            variant="tertiary"
          />
        </DialogClose>
      </DialogHeader>
      <DialogContent className={s.content}>
        <SearchBox setPostalCode={onUpdatePostalCode} />
        {missingMethodError && (
          <MessageWrapper>
            <Text
              langKey="pdx.delivery.location_picker.no_method.body"
              replacementTokens={{
                postal_code:
                  postalCodeHistory[postalCodeHistory.length - 2] ?? '',
                method:
                  deliveryOptions?.find((e) => e.id === selectedMethod)
                    ?.title ?? '',
              }}
            />
          </MessageWrapper>
        )}
        <Flex direction="column" padding={['px-24', 'pb-24']}>
          {isLoading && (
            <Loader
              repeat={
                locations?.length && locations?.length > 0
                  ? locations?.length
                  : undefined
              }
            />
          )}
          {!isLoading && locations?.length === 0 && <NoLocations />}
          {!isLoading &&
            locations?.map((location) => {
              return (
                <LocationButton
                  key={location.id}
                  {...location}
                  onSelect={onSelect}
                />
              );
            })}
        </Flex>
      </DialogContent>
    </Dialog>
  );
};
